import { Component, forwardRef, Injector, Input, OnInit, QueryList, ViewChildren, ViewEncapsulation } from '@angular/core';
import { map, startWith } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { NgFor, AsyncPipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { TlkSelectComponent, TlkSelectOptionComponent } from '@itero/ui-toolkit-angular/select';
import { ControlValueAccessorDirective } from '@shared/base-classes/control-value-accessor.directive';
import { idNameCompareWith } from '@shared/utils/selectors-utils';
import { IdName } from '@shared/models/id-name';
import { MouldElementComponent } from '../mould-element/mould-element.component';

const MOULDS_VALUE_ACCESSOR = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => MouldsDropdownComponent),
	multi: true
};

/**
 * Mould picker built directly on `tlk-select` because each option renders a mould image (projected
 * option content), which the generic `rx-select` wrapper does not support.
 */
@Component({
	selector: 'rx-moulds-dropdown',
	templateUrl: './moulds-dropdown.component.html',
	styleUrls: ['./moulds-dropdown.component.scss'],
	encapsulation: ViewEncapsulation.None,
	providers: [MOULDS_VALUE_ACCESSOR],
	standalone: true,
	imports: [TlkSelectComponent, TlkSelectOptionComponent, ReactiveFormsModule, NgFor, MouldElementComponent, AsyncPipe, TranslateModule]
})
export class MouldsDropdownComponent extends ControlValueAccessorDirective<IdName> implements OnInit {
	@Input() id: string;
	@Input() label: string;
	@Input() options: IdName[] = [];
	@Input() isDisabled = false;
	@Input() isReadOnly = false;
	@Input() isCancellable = false;

	@Input() compareWith: (o1: IdName, o2: IdName) => boolean = idNameCompareWith;

	@ViewChildren(TlkSelectOptionComponent) private readonly optionRefs: QueryList<TlkSelectOptionComponent<IdName>>;

	public selectedMouldId$: Observable<number>;

	constructor(injector: Injector) {
		super(injector);
	}

	ngOnInit(): void {
		this.selectedMouldId$ = this.control.valueChanges.pipe(
			startWith(this.control.value),
			map(value => value?.Id)
		);
	}

	handlePanelClose(): void {
		this.optionRefs?.forEach(opt => opt.setInactiveStyles());
	}

	getOptionValue = (option: IdName): string => option?.Name ?? '';

	/** Clears the selection. `tlk-select` also clears its own value on cancel. */
	handleCancelSelection(event: Event): void {
		event.stopPropagation();
		this.control.patchValue(null as unknown as IdName);
	}
}
